import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";
import { Currency, currenciesConfig } from "../../../constants";
import BottomSheet from "../../BottomSheet";

interface CurrencySelectorBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCurrency: Currency;
  onSelectCurrency: (currency: Currency) => void;
  mode: "base" | "quote";
  baseCurrency?: Currency; // Required when mode is 'quote'
}

interface CurrencyOption {
  value: Currency;
  label: string;
}

export default function CurrencySelectorBottomSheet({
  isOpen,
  onClose,
  selectedCurrency,
  onSelectCurrency,
  mode,
  baseCurrency,
}: CurrencySelectorBottomSheetProps) {
  const [searchQuery, setSearchQuery] = useState("");

  // Get available currencies based on mode
  const availableCurrencies = useMemo((): CurrencyOption[] => {
    if (mode === "base") {
      // For base currency, only show currencies that can send to others (have canSend array)
      return currenciesConfig
        .filter((config) => config.canSend.length > 0)
        .map((config) => ({
          value: config.currency,
          label: `${config.currency}`,
        }));
    } else {
      // For quote currency, show currencies the base currency can send to
      const baseConfig = currenciesConfig.find(
        (config) => config.currency === baseCurrency,
      );
      return baseConfig?.canSend || [];
    }
  }, [mode, baseCurrency]);

  // Filter currencies based on search query
  const filteredCurrencies = useMemo(() => {
    if (!searchQuery.trim()) return availableCurrencies;

    const query = searchQuery.toLowerCase();
    return availableCurrencies.filter(
      (currency) =>
        currency.value.toLowerCase().includes(query) ||
        currency.label.toLowerCase().includes(query),
    );
  }, [availableCurrencies, searchQuery]);

  const handleSelectCurrency = (currency: Currency) => {
    onSelectCurrency(currency);
    setSearchQuery(""); // Reset search
    onClose();
  };

  const handleClose = () => {
    setSearchQuery(""); // Reset search
    onClose();
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      width="w-full lg:ml-[calc(25vw+12rem)] lg:w-[calc(50vw-8rem)]"
    >
      <div className="currency-selector-bottom-sheet pb-6">
        {/* Header with close button */}
        <div className="flex items-center justify-between px-6 mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Select Currency
          </h2>
        </div>

        {/* Search Input */}
        <div className="relative mb-4 px-6">
          <MagnifyingGlassIcon className="absolute left-9 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search currencies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Currency List */}
        <div className="currency-list max-h-96 overflow-y-auto px-6">
          <AnimatePresence>
            {filteredCurrencies.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="py-12 text-center"
              >
                <p className="text-gray-500">No currencies found</p>
              </motion.div>
            ) : (
              <div className="space-y-1">
                {filteredCurrencies.map((currency, index) => (
                  <motion.button
                    key={currency.value}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ delay: index * 0.02 }}
                    onClick={() => handleSelectCurrency(currency.value)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                      selectedCurrency === currency.value
                        ? "bg-primary text-white"
                        : "hover:bg-gray-50 active:bg-gray-100"
                    }`}
                  >
                    <span
                      className={`font-medium ${
                        selectedCurrency === currency.value
                          ? "text-white"
                          : "text-gray-900"
                      }`}
                    >
                      {currency.label}
                    </span>

                    {selectedCurrency === currency.value && (
                      <CheckIcon className="w-5 h-5 text-white flex-shrink-0" />
                    )}
                  </motion.button>
                ))}
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </BottomSheet>
  );
}
